import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import * as z from 'zod';

import {
  BOLT_IMPERSONATE_COOKIE,
  UNDERWRITER_ROLES,
  USER_ROLE,
} from '~/common/constants';
import useUrl, { type ObfuscatedLoanIdentifier } from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { navigateTo } from '~/common/util/browserNavigation';

const claimsSchema = z.object({
  email: z.email(),
  destiny_id: z
    .string()
    .regex(/^\d+$/)
    .optional()
    .transform((id) => Number.parseInt(id)),
  name: z.string(),
  group: z.array(z.string()).or(z.string()).optional(),
  role: z.array(z.string()).or(z.string()).optional(),
  access_token: z.jwt(),
});

export function login() {
  navigateTo(
    `${globalThis.globalConfig?.api.endpoint_uri}/Account/Login?returnUrl=${encodeURIComponent(globalThis.location.href)}`,
  );
}

function isAxiosError(
  error: unknown,
): error is { status: number; isAxiosError: true } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    error.isAxiosError === true
  );
}

export default function useAuthentication() {
  const [isRedirect, setIsRedirect] = useState(false);

  const { isLoading, data, error, isError } = useQuery({
    queryKey: ['user'],
    async queryFn() {
      const response = await typedApiClient.account.userinfoList();
      const user = claimsSchema.parse(response.data);
      applyDataTracking(user);
      return user;
    },
    staleTime: 55 * 60 * 1_000, // 55 minutes
    cacheTime: Infinity,
    retry(failCount, error) {
      return isAxiosError(error) && error.status !== 401 && failCount < 3;
    },
  });

  useEffect(() => {
    if (!isError || !isAxiosError(error) || error.status !== 401) {
      return;
    }

    setIsRedirect(true); // Prevent blocked page from showing prematurely
    login();
  }, [isError, error]);

  return {
    isLoading: isLoading || isRedirect,
    isAuthorized: !!data,
    user: data || null,
  };
}

export function useUser() {
  const { isAuthorized, user } = useAuthentication();
  const { obfuscatedLoanIdentifier } = useUrl();
  const {
    hasAccessToUnderwriterView,
    canWriteUnderwriterCheckbox,
    canOverrideAmcCheckbox,
  } = useSelector(({ home }) => home.user);

  return useMemo(
    function () {
      if (!isAuthorized) return null;

      const isImpersonating = !!Cookies.get(
        BOLT_IMPERSONATE_COOKIE(
          obfuscatedLoanIdentifier?.obfuscatedLoanRecordId,
        ),
      );

      return {
        userId: user.destiny_id,
        email: user.email,
        role: [user.role].flat().filter(Boolean) as Array<
          (typeof USER_ROLE)[keyof typeof USER_ROLE]
        >,
        isImpersonating,
        isUnderwriter: checkForRoles(
          UNDERWRITER_ROLES,
          user,
          obfuscatedLoanIdentifier,
        ),
        isCloser: checkForRoles(
          [USER_ROLE.CLOSER, USER_ROLE.CLOSER_TL],
          user,
          obfuscatedLoanIdentifier,
        ),
        isTracUser: checkForRoles(
          [USER_ROLE.TRAC_SUPPORT, USER_ROLE.TRAC_ESCALATIONS],
          user,
          obfuscatedLoanIdentifier,
        ),
        hasAccessToUnderwriterView,
        canWriteUnderwriterCheckbox,
        canOverrideAmcCheckbox,
      };
    },
    [
      isAuthorized,
      user,
      obfuscatedLoanIdentifier,
      hasAccessToUnderwriterView,
      canWriteUnderwriterCheckbox,
      canOverrideAmcCheckbox,
    ],
  );
}

function applyDataTracking(user: ReturnType<typeof useAuthentication>['user']) {
  if (globalThis.dataTracking) {
    globalThis.dataTracking.role = Array.isArray(user.role)
      ? user.role[0]
      : user.role;
    globalThis.dataTracking.email = user.email;
    globalThis.dataTracking.name = user.name;
  }
  if (user.email && globalThis.dtrum?.identifyUser) {
    globalThis.dtrum.identifyUser(user.email);
  }
}

function checkForRoles(
  roles: string[],
  user: z.infer<typeof claimsSchema>,
  obfuscatedLoanIdentifier?: ObfuscatedLoanIdentifier,
) {
  const impersonationRole = Cookies.get(
    BOLT_IMPERSONATE_COOKIE(obfuscatedLoanIdentifier?.obfuscatedLoanRecordId),
  );

  const userRoles = impersonationRole
    ? [impersonationRole]
    : [user.role].flat();

  return roles.some((role) => userRoles.includes(role));
}
